import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { TrendingUp, Activity, Info, TrendingDown, Briefcase, Trophy, Newspaper, Shield, LogIn, LogOut, User, Sparkles } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import RecommendationsPage from './pages/RecommendationsPage';
import StockDetailPage from './pages/StockDetailPage';
import MostActivePage from './pages/MostActivePage';
import Week52LowsPage from './pages/Week52LowsPage';
import PortfolioPage from './pages/PortfolioPage';
import AboutPage from './pages/AboutPage';
import AdminUploadPage from './pages/AdminUploadPage';
import SmartRankingsPage from './pages/SmartRankingsPage';
import DailyNewsPage from './pages/DailyNewsPage';
import FinancialStrengthPage from './pages/FinancialStrengthPage';
import TiwiStockV2Page from './pages/TiwiStockV2Page';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="app-container">
          <AppContent />
        </div>
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  const [isDark, setIsDark] = useState(true);
  const { user, checking, logout } = useAuth();

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show login/register pages without sidebar
  if (!user) {
    return (
      <div className={isDark ? 'dark' : ''}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="flex min-h-screen bg-background text-foreground">
        {/* Desktop Vertical Sidebar */}
        <nav className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-card border-r border-border z-50 flex-col">
          <div className="p-6 border-b border-border">
            <img 
              src="/tiwistock-logo.png" 
              alt="TiwiStock" 
              className="w-full h-auto"
            />
          </div>
          
          <div className="flex-1 py-6 px-4 space-y-2 overflow-y-auto">
            <NavLink 
              to="/smart-rankings" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-yellow' : ''}`}
              data-testid="nav-smart-rankings"
            >
              <Trophy className="w-5 h-5" />
              <span className="font-medium">Smart Rankings</span>
            </NavLink>
            
            <NavLink 
              to="/" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-green' : ''}`}
              data-testid="nav-recommendations"
            >
              <TrendingUp className="w-5 h-5" />
              <span className="font-medium">Top Picks</span>
            </NavLink>
            
            <NavLink 
              to="/active" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-blue' : ''}`}
              data-testid="nav-active"
            >
              <Activity className="w-5 h-5" />
              <span className="font-medium">Active</span>
            </NavLink>
            
            <NavLink 
              to="/52week-lows" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-yellow' : ''}`}
              data-testid="nav-52week-lows"
            >
              <TrendingDown className="w-5 h-5" />
              <span className="font-medium">High Potential</span>
            </NavLink>
            
            <NavLink 
              to="/portfolio" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-purple' : ''}`}
              data-testid="nav-portfolio"
            >
              <Briefcase className="w-5 h-5" />
              <span className="font-medium">Portfolio</span>
            </NavLink>
            
            <NavLink 
              to="/news" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-blue' : ''}`}
              data-testid="nav-news"
            >
              <Newspaper className="w-5 h-5" />
              <span className="font-medium">Daily News</span>
            </NavLink>
            
            <NavLink 
              to="/financial-strength" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-blue' : ''}`}
              data-testid="nav-financial-strength"
            >
              <Shield className="w-5 h-5" />
              <span className="font-medium">Financial Strength</span>
            </NavLink>
            
            <NavLink 
              to="/about" 
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-gray' : ''}`}
              data-testid="nav-about"
            >
              <Info className="w-5 h-5" />
              <span className="font-medium">About</span>
            </NavLink>

            <NavLink
              to="/tiwistock-2"
              className={({ isActive }) => `nav-vertical ${isActive ? 'active-green' : ''}`}
              data-testid="nav-tiwistock-v2"
            >
              <Sparkles className="w-5 h-5 text-emerald-400" />
              <span className="font-medium flex items-center gap-1.5">
                Tiwistock-2
                <span className="text-[9px] px-1 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-bold tracking-wider">V2 ✨</span>
              </span>
            </NavLink>
          </div>
          
          <div className="p-4 border-t border-border">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate" data-testid="user-name">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Intelligent AI Stock Advisory
            </p>
          </div>
        </nav>
        
        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border backdrop-blur-lg z-50">
          <div className="flex items-center justify-around py-2 px-2">
            <NavLink 
              to="/smart-rankings" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-yellow' : ''}`}
              data-testid="nav-smart-rankings-mobile"
            >
              <Trophy className="w-5 h-5" />
              <span className="text-xs mt-1">Rankings</span>
            </NavLink>
            
            <NavLink 
              to="/" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-green' : ''}`}
              data-testid="nav-recommendations-mobile"
            >
              <TrendingUp className="w-5 h-5" />
              <span className="text-xs mt-1">Top Picks</span>
            </NavLink>
            
            <NavLink 
              to="/active" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-blue' : ''}`}
              data-testid="nav-active-mobile"
            >
              <Activity className="w-5 h-5" />
              <span className="text-xs mt-1">Active</span>
            </NavLink>
            
            <NavLink 
              to="/52week-lows" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-yellow' : ''}`}
              data-testid="nav-52week-lows-mobile"
            >
              <TrendingDown className="w-5 h-5" />
              <span className="text-xs mt-1">Potential</span>
            </NavLink>
            
            <NavLink 
              to="/portfolio" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-purple' : ''}`}
              data-testid="nav-portfolio-mobile"
            >
              <Briefcase className="w-5 h-5" />
              <span className="text-xs mt-1">Portfolio</span>
            </NavLink>
            
            <NavLink 
              to="/financial-strength" 
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-blue' : ''}`}
              data-testid="nav-financial-strength-mobile"
            >
              <Shield className="w-5 h-5" />
              <span className="text-xs mt-1">Strength</span>
            </NavLink>

            <NavLink
              to="/tiwistock-2"
              className={({ isActive }) => `nav-mobile ${isActive ? 'active-green' : ''}`}
              data-testid="nav-tiwistock-v2-mobile"
            >
              <Sparkles className="w-5 h-5 text-emerald-400" />
              <span className="text-xs mt-1">V2 ✨</span>
            </NavLink>
            
            <button
              onClick={logout}
              className="nav-mobile text-red-400"
              data-testid="logout-mobile"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-xs mt-1">Sign Out</span>
            </button>
          </div>
        </nav>
        
        {/* Main Content Area */}
        <div className="flex-1 md:ml-64 pb-20 md:pb-0">
          <Routes>
            <Route path="/" element={<ProtectedRoute><RecommendationsPage /></ProtectedRoute>} />
            <Route path="/smart-rankings" element={<ProtectedRoute><SmartRankingsPage /></ProtectedRoute>} />
            <Route path="/stock/:symbol" element={<ProtectedRoute><StockDetailPage /></ProtectedRoute>} />
            <Route path="/active" element={<ProtectedRoute><MostActivePage /></ProtectedRoute>} />
            <Route path="/52week-lows" element={<ProtectedRoute><Week52LowsPage /></ProtectedRoute>} />
            <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
            <Route path="/news" element={<ProtectedRoute><DailyNewsPage /></ProtectedRoute>} />
            <Route path="/financial-strength" element={<ProtectedRoute><FinancialStrengthPage /></ProtectedRoute>} />
            <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
            <Route path="/tiwistock-2" element={<ProtectedRoute><TiwiStockV2Page /></ProtectedRoute>} />
            <Route path="/admin/upload" element={<ProtectedRoute><AdminUploadPage /></ProtectedRoute>} />
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/register" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
